// src/mobile/MobileApp.tsx
/* Movatic WMS — Mobile App | CesarFilho 932048016 */
import React, { useEffect, useState } from 'react'
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom'
import {
  type LucideIcon,
  ScanLine,
  Boxes,
  Search,
  ClipboardList,
} from 'lucide-react'

import MobileShell from './components/MobileShell'
import TaskOfferProvider from './components/TaskOfferProvider'
import MobileScan from './pages/Scan'
import Receive from './pages/Receive'
import Ship from './pages/Ship'
import Move from './pages/Move'
import Count from './pages/Count'
import Lookup from './pages/Lookup'
import Tasks from './pages/Tasks'
import Execucao from './pages/Execucao'
import Areas from './pages/Areas'
import Area from './pages/Area'
import LoginScanGate from './components/LoginScanGate'
import { operatorMessage } from './lib/operatorMessage'
import { useAuthStore } from '@/store/authStore'

// Engole o Enter apenas em dispositivos Zebra (DataWedge modo teclado)
import { useZebraEnterSwallow } from './hooks/useZebraEnterSwallow'

/** Tipo de ícone aceito pelo MobileShell (size: number) */
type IconComponent = React.ComponentType<{ size?: number; className?: string }>

/** Wrapper para ícones do lucide → assinatura esperada pelo Shell */
function wrapIcon(Icon: LucideIcon): IconComponent {
  const Wrapped: IconComponent = ({ size, className }) => (
    <Icon size={size} className={className} />
  )
  Wrapped.displayName = `Icon(${Icon.displayName ?? 'Lucide'})`
  return Wrapped
}

/** Alias /m/sim/* → /m/*?sim=1 (em DEV resolvemos via Router) */
function SimAlias() {
  const loc = useLocation()
  // /m/sim/home -> rest="home"
  const rest = loc.pathname.replace(/^\/m\/sim\/?/, '')
  const hasQuery = loc.search?.length > 0
  const search = hasQuery ? `${loc.search}&sim=1` : '?sim=1'
  return <Navigate to={`/m/${rest || 'home'}${search}`} replace />
}

/** —— App Mobile (montado em /m/* pelo router principal) —— */
export default function MobileApp() {
  // Ativa o “swallow” do Enter só no coletor Zebra
  useZebraEnterSwallow()

  return (
    <Routes>
      {/* /m/sim... => /m/... ?sim=1 */}
      <Route path="sim/*" element={<SimAlias />} />

      {/* /m => /m/home */}
      <Route index element={<Navigate to="home" replace />} />

      {/* Demais rotas do app (relativas a /m/*) — atrás do gate de presença (F4) */}
      <Route
        path="/*"
        element={
          <PresenceGate>
            <MobileRoutes />
          </PresenceGate>
        }
      />
    </Routes>
  )
}

// Bearer p/ o /sessions/active/ (molde do LoginScanGate; token do auth store).
function presenceAuthHeaders(): HeadersInit {
  const state =
    (
      useAuthStore as unknown as { getState?: () => { accessToken?: string } }
    ).getState?.() ?? {}
  const headers: Record<string, string> = {}
  if (state.accessToken)
    headers['Authorization'] = `Bearer ${state.accessToken}`
  return headers
}

// ADR-0012-adenda F4 + F4b-9: gate de login-por-scan (vínculo operador↔coletor, 1×/turno + 1× no
// fim). Ao MONTAR consulta a sessão ativa (GET /sessions/active/ -> logged_in): se já há
// PresenceSession viva, SALTA o scan -> reabrir o PWA (ex.: após queda, retoma F4b-8) não re-pede
// COL-NNN. Fail-closed: /active a falhar (rede) OU logged_in=false -> mostra o LoginScanGate (scan
// legítimo de início de turno). Sem sessão o operador não passa às tarefas (D4).
function PresenceGate({ children }: { children: React.ReactNode }) {
  const [ok, setOk] = useState(false)
  const [checking, setChecking] = useState(true) // consulta /active ao montar (evita flicker do gate)
  // ADR-0058 F2: utilizador SEM Operator -> falha cedo (mensagem, sem tela de picar).
  const [notOperatorMsg, setNotOperatorMsg] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await fetch('/api/workforce/sessions/active/', {
          headers: presenceAuthHeaders(),
        })
        if (cancelled) return
        if (r.ok) {
          const data = await r.json()
          if (!cancelled && data?.logged_in === true) setOk(true) // sessão viva -> salta o scan
        } else if (r.status === 400) {
          // /active só devolve 400 quando o utilizador NÃO tem Operator (_require_operator).
          // Distingue-se de "sem sessão" (200 logged_in=false) -> falha cedo com a mensagem.
          let detail = ''
          try {
            const body = await r.json()
            detail = typeof body?.detail === 'string' ? body.detail : ''
          } catch {
            detail = ''
          }
          if (!cancelled)
            setNotOperatorMsg(
              operatorMessage(detail) ||
                'Esta conta não tem perfil de operador.',
            )
        }
        // Qualquer outro status (401/…) cai no fail-closed -> gate de scan (F4b-9).
      } catch {
        // Erro de rede -> fail-closed: mostra o gate (F4b-9).
      } finally {
        if (!cancelled) setChecking(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  if (checking) {
    return (
      <div className="coletor-root min-h-screen grid place-items-center text-sm opacity-70">
        A verificar a sessão…
      </div>
    )
  }
  // ADR-0058 F2: não é operador -> mensagem clara, SEM tela de picar nem scanner armado.
  if (notOperatorMsg) {
    return (
      <div className="coletor-root min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <ScanLine size={48} className="mb-3 opacity-40" />
        <h1 className="text-xl font-semibold">Sem perfil de operador</h1>
        <p className="mt-2 opacity-80">{notOperatorMsg}</p>
      </div>
    )
  }
  if (!ok) return <LoginScanGate onSuccess={() => setOk(true)} />
  return <>{children}</>
}

function MobileRoutes() {
  // rotas RELATIVAS (sem /m/ na frente)
  const tabs: { to: string; label: string; icon: IconComponent }[] = [
    { to: 'home', label: 'Início', icon: wrapIcon(Boxes) },
    { to: 'scan', label: 'Scan', icon: wrapIcon(ScanLine) },
    { to: 'lookup', label: 'Consulta', icon: wrapIcon(Search) },
    { to: 'tasks', label: 'Tarefas', icon: wrapIcon(ClipboardList) },
  ]

  // TaskOfferProvider envolve o shell: como MobileRoutes so' monta ATRAS do PresenceGate,
  // o popup + poll do inbox correm SEMPRE que ha' PresenceSession ativa, em qualquer aba.
  return (
    <TaskOfferProvider>
      <MobileShell tabs={tabs}>
        <Routes>
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<Areas />} />
          <Route path="scan" element={<MobileScan />} />
          <Route path="receive" element={<Receive />} />
          <Route path="ship" element={<Ship />} />
          <Route path="move" element={<Move />} />
          <Route path="count" element={<Count />} />
          <Route path="lookup" element={<Lookup />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="execucao" element={<Execucao />} />
          <Route path="area/:id" element={<Area />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </MobileShell>
    </TaskOfferProvider>
  )
}

function NotFound() {
  return (
    <div className="p-4">
      <div className="text-lg font-semibold mb-2">Rota não encontrada</div>
      <Link to="/m/home" className="btn">
        Voltar
      </Link>
    </div>
  )
}
